import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounce, Subject, timer } from 'rxjs';
import { UmssButtonComponent } from '../button/button.component';

type PageItem = number | 'ellipsis-left' | 'ellipsis-right';

@Component({
  selector: 'umss-pagination',
  standalone: true,
  imports: [UmssButtonComponent, ReactiveFormsModule],
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UmssPaginationComponent implements AfterViewInit, OnDestroy {
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly pendingPage$ = new Subject<number>();
  private resizeObserver?: ResizeObserver;

  readonly currentPage = input(1);
  readonly totalPages = input(1);
  readonly debounceMs = input(350);
  readonly skeleton = input(false);
  readonly pageChange = output<number>();
  readonly pageChangeStart = output<number>();

  // Optimistic display page — updates immediately on click, synced back from input
  readonly displayPage = signal(1);

  // Responsive state driven by ResizeObserver
  readonly sideWindow = signal(2);
  readonly showLabels = signal(true);
  readonly activeInput = signal(false);
  readonly activeInputValue = new FormControl('');

  readonly pageInput = viewChild('pageInput', { read: ElementRef });

  constructor() {
    // Sync parent input → displayPage when it changes externally
    effect(() => {
      this.displayPage.set(this.currentPage());
    });

    this.pendingPage$
      .pipe(
        debounce(() => timer(this.debounceMs())),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((page) => this.pageChange.emit(page));
  }

  readonly pageItems = computed<PageItem[]>(() => {
    const total = this.totalPages();
    const current = this.displayPage();
    const side = this.sideWindow();

    const pagesSet = new Set<number>();

    // Always show first 2
    for (let p = 1; p <= Math.min(side || 1, total); p++) pagesSet.add(p);

    // Window around current page
    for (
      let p = Math.max(1, current - side);
      p <= Math.min(total, current + side);
      p++
    )
      pagesSet.add(p);

    // Always show last 2
    for (let p = Math.max(1, total - 1 + 2 - (side || 1)); p <= total; p++)
      pagesSet.add(p);

    const sorted = Array.from(pagesSet).sort((a, b) => a - b);

    const items: PageItem[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
        // Left-side gap: between first pages and the window
        // Right-side gap: between the window and the last pages
        items.push(
          sorted[i - 1] < current ? 'ellipsis-left' : 'ellipsis-right'
        );
      }
      items.push(sorted[i]);
    }

    return items;
  });

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? 0;
        // Run inside Angular zone so signal writes trigger change detection
        this.zone.run(() => {
          let side = 2;
          if (width < 620) {
            side = 1;
          }
          if (width < 480) {
            side = 0;
          }
          this.sideWindow.set(side);
          this.showLabels.set(width >= 480);
        });
      });
      this.resizeObserver.observe(this.hostEl.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  onPrevious(): void {
    const display = this.displayPage();
    if (display > 1) {
      this.displayPage.set(display - 1);
      this.emitPage(display - 1);
    }
  }

  onNext(): void {
    const display = this.displayPage();
    if (display < this.totalPages()) {
      this.displayPage.set(display + 1);
      this.emitPage(display + 1);
    }
  }

  onPageClick(page: number): void {
    if (page !== this.displayPage()) {
      this.displayPage.set(page);
      this.emitPage(page);
    }
  }

  onCurrentPageClick(): void {
    this.activeInput.set(true);
    setTimeout(() => this.pageInput()?.nativeElement.focus());
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.commitPage();
    } else if (event.key === 'Escape') {
      this.clearInput();
    }
  }

  onInputBlur(): void {
    this.commitPage();
  }

  private commitPage(): void {
    const value = this.activeInputValue.value;
    if (!value) {
      this.activeInput.set(false);
      return;
    }
    const page = parseInt(value, 10);
    if (
      !isNaN(page) &&
      page >= 1 &&
      page <= this.totalPages() &&
      page !== this.displayPage()
    ) {
      this.displayPage.set(page);
      this.emitPage(page);
    }
    this.clearInput();
  }

  private clearInput(): void {
    this.activeInputValue.setValue('');
    this.activeInput.set(false);
  }

  private emitPage(page: number): void {
    this.pageChangeStart.emit(page);
    this.pendingPage$.next(page);
  }
}
